import { Paper, Typography, TextField, Grid, IconButton, Button, Box, Select, Checkbox, MenuItem } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import weapons from '../data/weapons.json'
import SymbolField from "../Util/SymbolField";
import { BuiltInAttachmentsField, CategoriesField, weaponQualityOptions } from './ModEditors'
import { hasAstromechSocket } from '../Util/VehicleNavicomputer'

const FIRING_ARCS = ["Fore", "Aft", "Port", "Starboard", "Dorsal", "Ventral"];

const VehicleWeaponEditor = ({ weapon, qualities, onChange, onRemove }) => {
    const arcs = weapon.FiringArcs || {};
    const weaponQualities = Array.isArray(weapon.Qualities) ? weapon.Qualities : [];
    const setField = (patch) => onChange({ ...weapon, ...patch });

    return (
        <Grid item xs={12} sx={{ margin: "7.5px" }}>
          <Paper variant="outlined" sx={{ padding: "10px" }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
                <TextField label="Count" sx={{ maxWidth: "80px" }} value={weapon.Count || ""} onChange={(e) => { if (!isNaN(e.target.value)) { setField({ Count: e.target.value || undefined }) } }} />
                <TextField label="Name" sx={{ minWidth: "180px" }} value={weapon.Name || ""} onChange={(e) => setField({ Name: e.target.value })} />
                <TextField label="Damage" sx={{ maxWidth: "90px" }} value={weapon.Damage || ""} onChange={(e) => setField({ Damage: e.target.value })} />
                <TextField label="Crit" sx={{ maxWidth: "80px" }} value={weapon.Crit || ""} onChange={(e) => setField({ Crit: e.target.value })} />
                <TextField label="Range" sx={{ maxWidth: "120px" }} value={weapon.Range || ""} onChange={(e) => setField({ Range: e.target.value })} />
                <IconButton onClick={onRemove} sx={{ maxWidth: "30px" }} size="small" className="no-print"><CloseIcon /></IconButton>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", marginTop: "5px" }}>
                <Typography sx={{ marginRight: "5px" }}>Fire Arc:</Typography>
                {FIRING_ARCS.map((arc) => (
                    <Box key={arc} sx={{ display: "flex", alignItems: "center" }}>
                        <Checkbox
                            size="small"
                            checked={arcs[arc] === "true"}
                            onChange={(e) => {
                                const na = { ...arcs };
                                if (e.target.checked) { na[arc] = "true"; } else { delete na[arc]; }
                                setField({ FiringArcs: Object.keys(na).length ? na : undefined });
                            }}
                        />
                        <Typography variant="body2">{arc}</Typography>
                    </Box>
                ))}
                <Box sx={{ display: "flex", alignItems: "center", marginLeft: "12px" }}>
                    <Checkbox size="small" checked={weapon.Turret === "true"} onChange={(e) => setField({ Turret: e.target.checked ? "true" : undefined })} />
                    <Typography variant="body2">Turret</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", marginLeft: "12px" }}>
                    <Checkbox size="small" checked={weapon.Scale === "wsPersonal"} onChange={(e) => setField({ Scale: e.target.checked ? "wsPersonal" : undefined })} />
                    <Typography variant="body2">Personal Scale</Typography>
                </Box>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px", marginTop: "5px" }}>
                <Typography sx={{ marginRight: "5px" }}>Qualities:</Typography>
                {weaponQualities.map((q, qi) => (
                    <Box key={qi} sx={{ display: "flex", alignItems: "center" }}>
                        <Select
                            value={q.Key || ""}
                            displayEmpty
                            onChange={(e) => setField({ Qualities: weaponQualities.map((qq, i) => (i === qi ? { ...qq, Key: e.target.value } : qq)) })}
                            sx={{ minWidth: "150px" }}
                        >
                            <MenuItem value=""><em>Quality</em></MenuItem>
                            {weaponQualityOptions(qualities, q.Key).map((ql) => (<MenuItem key={ql.Key} value={ql.Key}>{ql.Name}</MenuItem>))}
                        </Select>
                        <TextField
                            label="Rating"
                            value={q.Count || ""}
                            sx={{ maxWidth: "80px", marginLeft: "4px" }}
                            onChange={(e) => setField({
                                Qualities: weaponQualities.map((qq, i) => {
                                    if (i !== qi) { return qq; }
                                    const c = { ...qq };
                                    if (e.target.value === "") { delete c.Count; } else { c.Count = e.target.value; }
                                    return c;
                                }),
                            })}
                        />
                        <IconButton onClick={() => setField({ Qualities: weaponQualities.filter((_, i) => i !== qi) })} sx={{ maxWidth: "30px" }} size="small" className="no-print"><CloseIcon /></IconButton>
                    </Box>
                ))}
                <Button onClick={() => setField({ Qualities: [...weaponQualities, {}] })} className="no-print">Add Quality</Button>
            </Box>
          </Paper>
        </Grid>
    );
};

const VehicleEdit = ({ editedItem, index, appFiles, setAppFiles }) => {
    const talents = JSON.parse(appFiles.talents)
    const attachments = appFiles.attachments ? JSON.parse(appFiles.attachments) : []
    const qualities = appFiles.qualities ? JSON.parse(appFiles.qualities) : []

    const handleEdit = (newItem) => {
        let newFiles = { ...appFiles };
        let newCat = JSON.parse(appFiles["vehicles"]);
        newCat[index] = newItem;
        newFiles["vehicles"] = JSON.stringify(newCat);
        setAppFiles(newFiles);
        localStorage.setItem('appFiles', JSON.stringify(newFiles));
    };

    if (editedItem.BaseMods && !Array.isArray(editedItem.BaseMods)) { editedItem.BaseMods = [editedItem.BaseMods] }

    return (
        <Paper>
            <TextField
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Price"
                value={editedItem?.Price || 0}
                onChange={(e) =>
                    handleEdit({ ...editedItem, Price: e.target.value })
                }
            />
            <TextField
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Rarity"
                value={editedItem?.Rarity || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, Rarity: e.target.value })
                }
            />
            <TextField
                fullWidth
                sx={{ marginTop: "10px" }}
                label="Type"
                value={editedItem?.Type || ""}
                onChange={(e) => handleEdit({ ...editedItem, Type: e.target.value })}
            />
            <TextField
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Manufacturer"
                value={editedItem?.Manufacturer || ""}
                onChange={(e) => handleEdit({ ...editedItem, Manufacturer: e.target.value })}
            />
            <TextField
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Model"
                value={editedItem?.Model || ""}
                onChange={(e) => handleEdit({ ...editedItem, Model: e.target.value })}
            />
            <CategoriesField
                categories={editedItem?.Categories}
                resetKey={index}
                sx={{ marginTop: "10px" }}
                onCommit={(next) => handleEdit({ ...editedItem, Categories: next })}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                <span>Restricted:</span>
                <Checkbox
                    checked={!!editedItem?.Restricted}
                    onChange={(e) => handleEdit({ ...editedItem, Restricted: e.target.checked })}
                />
            </Box>
            <BuiltInAttachmentsField
                value={editedItem?.BuiltInAttachments}
                onChange={(v) => handleEdit({ ...editedItem, BuiltInAttachments: v })}
                attachments={attachments}
                type="Vehicle"
            />
            <TextField
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Crew"
                value={editedItem?.Crew || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, Crew: e.target.value })
                }
            />
            <TextField
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Passengers"
                value={editedItem?.Passengers || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, Passengers: e.target.value })
                }
            />
            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                <span>Navicomputer:</span>
                <Checkbox
                    checked={editedItem?.NaviComputer || false}
                    onChange={(e) => handleEdit({ ...editedItem, NaviComputer: e.target.checked, AstromechSocket: e.target.checked ? false : editedItem?.AstromechSocket })}
                />
                {!editedItem?.NaviComputer && <>
                    <span>Astromech Droid Socket:</span>
                    <Checkbox
                        checked={hasAstromechSocket(editedItem)}
                        onChange={(e) => handleEdit({ ...editedItem, AstromechSocket: e.target.checked })}
                    />
                </>}
            </Box>
            <TextField
                fullWidth
                sx={{ marginTop: "10px" }}
                label="Navicomputer Note (e.g. limited jumps)"
                value={editedItem?.NaviComputerNote || ""}
                onChange={(e) => handleEdit({ ...editedItem, NaviComputerNote: e.target.value || undefined })}
            />
            <TextField
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Primary Hyperdrive Class"
                value={editedItem?.HyperdrivePrimary || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, HyperdrivePrimary: e.target.value })
                }
            />
            <TextField
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Backup Hyperdrive Class"
                value={editedItem?.HyperdriveBackup || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, HyperdriveBackup: e.target.value })
                }
            />
            <TextField
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Tertiary Hyperdrive Class"
                value={editedItem?.HyperdriveTertiary || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, HyperdriveTertiary: e.target.value || undefined })
                }
            />
            <TextField
                fullWidth
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Sensor Range"
                value={editedItem?.SensorRange || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, SensorRange: e.target.value })
                }
            />
            <TextField
                fullWidth
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Hard Points"
                value={editedItem?.HP || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, HP: e.target.value })
                }
            />
            <TextField
                fullWidth
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Encumbrance Capacity"
                value={editedItem?.EncumbranceCapacity || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, EncumbranceCapacity: e.target.value })
                }
            />
            <TextField
                fullWidth
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Consumables"
                value={editedItem?.Consumables || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, Consumables: e.target.value })
                }
            />
            <TextField
                fullWidth
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Speed"
                value={editedItem?.Speed || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, Speed: e.target.value })
                }
            />
            <TextField
                fullWidth
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Handling"
                value={editedItem?.Handling || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, Handling: e.target.value })
                }
            />
            <TextField
                fullWidth
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Silhouette"
                value={editedItem?.Silhouette || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, Silhouette: e.target.value })
                }
            />
            <TextField
                fullWidth
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Armor"
                value={editedItem?.Armor || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, Armor: e.target.value })
                }
            />
            <TextField
                fullWidth
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Defense Fore"
                value={editedItem?.DefFore || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, DefFore: e.target.value })
                }
            />
            <TextField
                fullWidth
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Defense Aft"
                value={editedItem?.DefAft || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, DefAft: e.target.value })
                }
            />
            <TextField
                fullWidth
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Defense Port"
                value={editedItem?.DefPort || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, DefPort: e.target.value })
                }
            />
            <TextField
                fullWidth
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Defense Starboard"
                value={editedItem?.DefStarboard || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, DefStarboard: e.target.value })
                }
            />
            <TextField
                fullWidth
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="Hull Trauma Threshold"
                value={editedItem?.HullTrauma || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, HullTrauma: e.target.value })
                }
            />
            <TextField
                fullWidth
                sx={{
                    marginTop: "10px",
                    width: "50%",
                }}
                label="System Strain Threshold"
                value={editedItem?.SystemStrain || ""}
                onChange={(e) =>
                    handleEdit({ ...editedItem, SystemStrain: e.target.value })
                }
            />
            <TextField
                fullWidth
                sx={{ marginTop: "10px", width: "50%" }}
                label="Massive"
                value={editedItem?.Massive || ""}
                onChange={(e) => {
                    if (!isNaN(e.target.value)) { handleEdit({ ...editedItem, Massive: e.target.value || undefined }) }
                }}
            />
            <TextField
                fullWidth
                sx={{ marginTop: "10px", width: "50%" }}
                label="Max Altitude"
                value={editedItem?.MaxAltitude || ""}
                onChange={(e) => handleEdit({ ...editedItem, MaxAltitude: e.target.value })}
            />
            <TextField
                fullWidth
                sx={{ marginTop: "10px", width: "50%" }}
                label="Diving Depth"
                value={editedItem?.DivingDepth || ""}
                onChange={(e) => handleEdit({ ...editedItem, DivingDepth: e.target.value || undefined })}
            />
            <TextField
                fullWidth
                sx={{ marginTop: "10px" }}
                label="Carried Starfighters / Craft"
                value={editedItem?.StarFighters || ""}
                onChange={(e) => handleEdit({ ...editedItem, StarFighters: e.target.value })}
            />
            <Typography>Base Mods:</Typography>
            {editedItem.BaseMods && (
                <Grid container>
                    {editedItem.BaseMods.map((mod, index) => (
                        <Grid item key={index} sx={{ margin: "7.5px" }}>
                            {(mod.MiscDesc || mod.MiscDesc === "") &&
                                <>
                                    <Typography>Custom Mod:</Typography>
                                    <SymbolField
                                        label="Description"
                                        sx={{ minWidth: "500px" }}
                                        value={mod.MiscDesc}
                                        onChange={(e) => {
                                            let newMods = [...editedItem.BaseMods];
                                            newMods[index].MiscDesc = e.target.value;
                                            handleEdit({ ...editedItem, BaseMods: newMods });
                                        }}
                                    />
                                </>
                            }
                            <IconButton onClick={(e) => {
                                let newMods = [...editedItem.BaseMods];
                                newMods.splice(index, 1)
                                handleEdit({ ...editedItem, BaseMods: newMods });
                            }}
                                sx={{ maxWidth: "30px" }}
                                size="small"
                                className="no-print"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                    ))}
                </Grid>
            )}
            <Button onClick={(e) => {
                let newMods = [...editedItem.BaseMods || []];
                newMods.push({ MiscDesc: "" })
                handleEdit({ ...editedItem, BaseMods: newMods });
            }}
                className="no-print"
            >
                Add Custom Mod
            </Button>
            <Typography>Weapons:</Typography>
            {(editedItem.VehicleWeapons || editedItem.WeaponModifiers) && (
                <Grid container>
                    {editedItem.VehicleWeapons && editedItem.VehicleWeapons.map((weapon, index) => {
                        let lookupWeapon = {}
                        if (weapon.Key && !weapon.Name) {
                            lookupWeapon = weapons.find(w => w.Key === weapon.Key) || {}
                        }
                        const effWeapon = { ...lookupWeapon, ...weapon }

                        const handleWeaponChange = (updated) => {
                            let newVehicleWeapons = [...editedItem.VehicleWeapons];
                            let converted = { ...updated };
                            delete converted.Key;
                            newVehicleWeapons[index] = converted;
                            handleEdit({ ...editedItem, VehicleWeapons: newVehicleWeapons });
                        };

                        return (
                            <VehicleWeaponEditor
                                key={index}
                                weapon={effWeapon}
                                qualities={qualities}
                                onChange={handleWeaponChange}
                                onRemove={() => {
                                    let newWeapons = [...editedItem.VehicleWeapons];
                                    newWeapons.splice(index, 1);
                                    handleEdit({ ...editedItem, VehicleWeapons: newWeapons });
                                }}
                            />
                        );
                    })}

                    {editedItem.WeaponModifiers && editedItem.WeaponModifiers.map((modWeapon, index) => (
                        <VehicleWeaponEditor
                            key={index}
                            weapon={modWeapon}
                            qualities={qualities}
                            onChange={(updated) => {
                                let newModifiers = [...editedItem.WeaponModifiers];
                                newModifiers[index] = updated;
                                handleEdit({ ...editedItem, WeaponModifiers: newModifiers });
                            }}
                            onRemove={() => {
                                let newModifiers = [...editedItem.WeaponModifiers];
                                newModifiers.splice(index, 1);
                                handleEdit({ ...editedItem, WeaponModifiers: newModifiers });
                            }}
                        />
                    ))}
                </Grid>
            )}

            <Button onClick={(e) => {
                let newWeapons = [...editedItem.VehicleWeapons || []];
                newWeapons.push({})
                handleEdit({ ...editedItem, VehicleWeapons: newWeapons });
            }}
                className="no-print"
            >
                Add Weapon
            </Button>
        </Paper>
    )
}

export default VehicleEdit